import React, { useContext } from 'react';
import {
  Avatar,
  makeStyles,
  Theme,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import { UserAvatarFilled } from '@carbon/icons-react';
import { getFileUrl } from '@utils/urls';
import {
  CapaCapabilitiesStatusType,
  CapaQuotationServiceType
} from '@generated/graphql';
import { requestEnumToString } from '@utils/enumToString';
import { colorSet } from '@components/Provider';
import { CustomizedTypography } from '@components/Customized';
import {
  CompanyInfoContext,
  CompanyInfoContextValue
} from '../context/CompanyInfoContext';
import CompanySideButtons from './CompanySideButtons';
import { StarFilled } from '@carbon/icons-react';

interface CompanyNameStyleProps {
  isRequest: boolean;
}

const CompanyName = React.forwardRef<HTMLDivElement>((_, ref) => {
  const isRequest = window.location.pathname.includes('/dashboard');
  const theme = useTheme();
  const matchesMd = useMediaQuery(theme.breakpoints.down('md'));
  const {
    nameConatiner,
    companyThumbnail,
    nameInfo,
    companybuttons,
    companyThumbnailOutline,
    clickableReviewCount
  } = useStyles({ isRequest });
  const context = useContext<CompanyInfoContextValue | null>(
    CompanyInfoContext
  );

  if (!context) return null;

  const partnerInfo = context.partnerInfo;
  const logo = partnerInfo?.logo;
  const reviewAveragePoint = partnerInfo?.reviews?.reviewAveragePoint ?? 0.0;
  const reviewCount = partnerInfo?.reviews?.reviewCount ?? 0;
  const servicesText = (partnerInfo?.capabilities?.services ?? [])
    .filter((service) => service.status === CapaCapabilitiesStatusType.Active)
    .map((service) =>
      requestEnumToString(service.service as CapaQuotationServiceType)
    )
    .join(', ');

  const handleClickableReview = () => {
    const targetEl = document.getElementById('review');

    if (targetEl) {
      targetEl.scrollIntoView();
    }
  };

  return (
    <div className={nameConatiner} id="introduction" ref={ref}>
      <div className={companyThumbnailOutline}>
        <div className={companyThumbnail}>
          {logo ? (
            <Avatar src={getFileUrl(logo?.fileId ?? '')} />
          ) : (
            <div className="no-logo">
              <UserAvatarFilled size={63} color={colorSet.gray600} />
            </div>
          )}
        </div>
      </div>

      <div className={nameInfo}>
        <CustomizedTypography
          variant={!matchesMd ? 'h3' : 'h4'}
          fontWeight="700"
          color="textPrimary"
          align={!matchesMd ? undefined : 'center'}
        >
          {partnerInfo?.organizationName}
        </CustomizedTypography>

        {!matchesMd ? (
          <CustomizedTypography
            variant={!matchesMd ? 'body1' : 'body2'}
            color="textPrimary"
            align={!matchesMd ? undefined : 'center'}
            fontWeight="500"
          >
            <StarFilled
              color={colorSet.primary500}
              size={20}
              className="star"
            />
            {reviewAveragePoint.toFixed(1)} ·{' '}
            <span
              className={clickableReviewCount}
              onClick={handleClickableReview}
            >
              리뷰 {reviewCount}개
            </span>
            {servicesText === '' ? '' : ` · ${servicesText}`}
          </CustomizedTypography>
        ) : (
          <>
            <CustomizedTypography
              variant={'body1'}
              color="textPrimary"
              align={'center'}
              gutterBottoms={8}
              fontWeight="500"
            >
              <StarFilled
                color={colorSet.primary500}
                size={19}
                className="star"
              />
              {reviewAveragePoint.toFixed(1)} ·{' '}
              <span
                className={clickableReviewCount}
                onClick={handleClickableReview}
              >
                리뷰 {reviewCount}개
              </span>
            </CustomizedTypography>
            <CustomizedTypography
              variant={'body2'}
              color="textPrimary"
              align={'center'}
              fontWeight="500"
            >
              {servicesText}
            </CustomizedTypography>
          </>
        )}
      </div>
      {!isRequest && (
        <div className={companybuttons}>
          <CompanySideButtons />
        </div>
      )}
    </div>
  );
});

export default CompanyName;

const useStyles = makeStyles<Theme, CompanyNameStyleProps>((theme) => ({
  nameConatiner: {
    width: '100%',
    display: 'flex',
    position: 'relative',
    gap: '16px',
    alignItems: 'center',
    bottom: '8%',
    marginBottom: 16,
    [theme.breakpoints.down('md')]: {
      height: 'auto',
      flexDirection: 'column',
      alignItems: 'center',
      bottom: '6%',
      maxWidth: 626,
      margin: '0 auto'
    },
    [theme.breakpoints.down('xs')]: {
      height: ({ isRequest }) => (isRequest ? 'auto' : '316px'),
      flexDirection: 'column',
      alignItems: 'center',
      background: '#fff',
      bottom: '3%',
      borderRadius: '16px 16px 0 0',
      padding: 16
    }
  },
  clickableReviewCount: {
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  companyThumbnailOutline: {
    minWidth: 148,
    height: 148,
    position: 'relative',
    borderRadius: '50%',
    boxSizing: 'content-box',
    border: `8px solid #ffff`,
    background: '#fff',
    [theme.breakpoints.down('xs')]: {
      border: 'none',
      minWidth: 92,
      height: 92,
      marginTop: 10
    }
  },
  companyThumbnail: {
    minWidth: 148,
    height: 148,
    position: 'relative',
    borderRadius: '50%',
    boxSizing: 'border-box',
    border: `1px solid ${colorSet.gray400}`,
    [theme.breakpoints.down('xs')]: {
      minWidth: 92,
      height: 92,
      marginTop: 5
    },
    '& > div': {
      width: '100%',
      height: '100%',
      [theme.breakpoints.down('xs')]: {
        width: '100%',
        height: '100%'
      }
    },
    '& .no-logo': {
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      backgroundColor: colorSet.gray200,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }
  },
  nameInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'cneter',
    justifyContent: 'center',
    marginTop: 28,
    gap: '2px',
    [theme.breakpoints.down('md')]: {
      marginTop: 0
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 0
    },
    '& .star': {
      position: 'relative',
      bottom: '-2px',
      marginRight: 3,
      [theme.breakpoints.down('xs')]: {
        bottom: '-2px',
        position: 'relative',
        marginRight: 5
      }
    }
  },
  companybuttons: {
    marginLeft: 'auto',
    marginTop: 35,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      marginTop: 11,
      width: '100%'
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 'auto',
      width: '100%'
    }
  }
}));
